'use client';

import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import type { SxProps, Theme } from '@mui/material/styles';

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  keepMounted?: boolean;
  disableBackdropClose?: boolean;
  slotProps?: { paper?: NonNullable<React.ComponentProps<typeof Dialog>['slotProps']>['paper'] };
  sx?: SxProps<Theme>;
};

const ModalComponent: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  fullWidth = true,
  maxWidth = 'sm',
  keepMounted = true,
  disableBackdropClose = false,
  slotProps,
  sx,
}) => {
  const closeButtonRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    if (!open) return;

    const focusTimer = globalThis.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    return () => globalThis.clearTimeout(focusTimer);
  }, [open]);

  const handleClose = (_event?: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (disableBackdropClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      keepMounted={keepMounted}
      slotProps={slotProps}
      sx={{ 
        zIndex: 9999,
        ...sx 
      }}
    >
      {title && (
        <DialogTitle sx={{ m: 0, pr: 5 }}>
          {title}
        </DialogTitle>
      )}
      <IconButton
        ref={closeButtonRef}
        aria-label="Close"
        onClick={onClose}
        size="small"
        sx={{ position: 'absolute', right: 8, top: 8 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent dividers>{children}</DialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
};

export default ModalComponent;